/**
 * Device enrolment — the staff-facing half of the egress [03-us-kad-6a, 03-us-kad-6b, 03-us-kad-6c].
 *
 * Two routes, and they are the only two the api leaves public [KAD-6a AC-4]:
 *
 * ```
 * GET  /enrolment/session   204 — sets the layer-1 lock token as an HttpOnly cookie. No body, ever.
 * POST /enrolment           200 — enrolled; 401 | 403 | 429 — one of three refusals; 429 — or the floor
 * ```
 *
 * ⭐ **And two SCOPED routes, added by [03-us-kad-6d] and [03-us-kad-6i]:**
 *
 * ```
 * PUT  /enrolment/name      200 {name} — named; 409 — taken at this venue; 401 — the credential is dead
 * GET  /enrolment/me        200 — the LIVE enrolment behind the credential; 401 — there is none; 503
 * ```
 *
 * ⛔ **Neither is a third public route.** Both act on the enrolment the credential already names — the
 * first writes its name, which is why the name never rides on `POST /enrolment` [KAD-6a AC-4]; the second
 * only READS it, and is guarded by `KioskSessionGuard` like every other scoped call [decision 0280].
 *
 * ⛔ **Nothing here goes through `kioskFetch`.** That wrapper reports a 401 as *this device is not
 * enrolled* and returns the app to the PIN pad [decision 0245]; on `POST /enrolment` a 401 means *that PIN
 * was wrong* [decision 0090], and a screen that answered itself would loop. ⭐ **It takes the TRANSPORT half
 * instead** — `credentialedFetch` — so the credential is still not something either call can forget, and
 * the layer-1 token and the session cookie are the whole point of them.
 *
 * ⛔ **The response body is never a credential.** The session id is a `Set-Cookie` the script cannot read
 * [0221], so there is nothing here to store and nothing to put in web storage [AC-11].
 */
import type { Language } from '@/i18n/dictionary';
import { LANGUAGE } from '@/i18n/dictionary';

import type { EnrolmentRefusalOutcome } from './enrolment.contract';
import { ENROLMENT_REFUSAL } from './enrolment.contract';
import { credentialedFetch, kioskFetch } from './kioskFetch';

const SESSION_PATH = '/enrolment/session';
const ENROL_PATH = '/enrolment';
const NAME_PATH = '/enrolment/name';
const ME_PATH = '/enrolment/me';

/** The PIN screen's two calls are typed by a staffer at a tablet; 10s is the app's standing bound. */
const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/**
 * Whether the browser now holds [decision 0203]'s layer-1 enrolment token.
 *
 * ⭐ **`unavailable` is not a screen, it is a REFUSAL TO SUBMIT.** The three-strike device lock is keyed to
 * this token, so a pad that submitted without one would be a pad whose failures count against nothing
 * [KAD-6c AC-1b; KAD-6b AC-1] — the security control silently absent while every screen still looks right.
 *
 * ⭐ **`rate-limited` is a third answer because the coarse floor counts THIS request too** — it guards the
 * whole controller [0231; 0243] — and the screen it produces must be the floor's, not a generic failure
 * [`0262`; the Operator's *nothing disclosed* ruling].
 */
export type EnrolmentTokenOutcome = 'issued' | 'rate-limited' | 'unavailable';

/**
 * Which call carried an enrolment answer — `connect` is `POST /enrolment`, the rest are the enrolment probe's
 * own {@link ProbeTrigger}. ⭐ **Only {@link readVenueLanguage} reads it**, and only to make a repeated
 * line readable: *every tablet on its first load* is a deployment window, *one tablet after a staffer
 * typed six digits* is a venue.
 */
type LanguageAnswerSource = 'connect' | ProbeTrigger;

export async function issueEnrolmentToken(): Promise<EnrolmentTokenOutcome> {
  let response: Response;
  try {
    response = await credentialedFetch(`${apiBaseUrl()}${SESSION_PATH}`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (cause: unknown) {
    reportTokenFailure('the request never completed', cause);
    return 'unavailable';
  }

  /*
   * ⛔ **204 AND NOTHING ELSE — `response.ok` is the wrong test, and the difference is the whole control.**
   *
   * The api answers this route with `204` or not at all. But `staticwebapp.config.json` rewrites an unknown
   * path to `index.html` with a **200**, exactly as Vite's dev server did before `/enrolment` was added to
   * the proxy — so `ok` accepts a page of HTML as a lock token. The app would then record a token it does
   * not hold, submit PINs against **no layer-1 subject**, receive `attemptsLeft: null` every time, and
   * [0203]'s three-strike device lock would never fire — with every screen still looking right. ⭐ **A
   * misrouted deployment must fail loudly here rather than silently disarm the lock.**
   */
  if (response.status === 204) return 'issued';

  /*
   * ⭐ **A 429 here is the COARSE FLOOR, and it must render the floor's copy** [0231; 0243; `0262`].
   *
   * The floor is a class-level `ThrottlerGuard` counting REQUESTS on the whole controller, so it counts
   * this GET too — while layer 2 only ever runs inside the enrol handler. Reporting it as `unavailable`
   * would put *"We are having some issues"* on screen for the floor and *"Too many attempts"* for layer 2,
   * and ⛔ **which control refused would be readable off the screen** — the one thing the Operator's
   * *nothing disclosed* ruling removes.
   */
  if (response.status === 429) return 'rate-limited';

  reportTokenFailure(`the route answered ${String(response.status)}`, undefined);
  return 'unavailable';
}

/**
 * ⭐ **The one signal that says the three-strike lock is not counting.**
 *
 * Without a token every attempt comes back `attemptsLeft: null` and [0203]'s layer 1 has no subject — a
 * security control absent while every screen still looks right. ⛔ **Nothing about the attempt is logged**:
 * no PIN, no venue, no identifier. Same shape as `arrivalReason.ts`'s refusal alert, for the same reason.
 */
function reportTokenFailure(reason: string, cause: unknown): void {
  console.error({
    msg: 'the layer-1 enrolment token could NOT be issued — a PIN attempted now counts against no device lock',
    alert: 'enrolment_token_unavailable',
    reason,
    cause: cause instanceof Error ? cause.name : undefined,
  });
}

/**
 * What the server said about six digits.
 *
 * ⭐ **Five outcomes, because the server has five answers** — and the tablet renders the one it was given
 * rather than deriving it [KAD-6c § *The invariant*]:
 *
 * | | |
 * | --- | --- |
 * | `enrolled` | 200. `published` forks the flow exactly once [AC-7] |
 * | `incorrect-pin` | 401 `incorrect_pin`, with what this browser has left. Identical whatever the PIN was [0090] |
 * | `device-locked` | 403 `device_locked` — layer 1, three strikes, an admin clears it and nothing else does |
 * | `throttled` | 429 `throttled` — layer 2, the address's backoff. ⛔ Temporary, self-clearing, NOT the lock |
 * | `rate-limited` | 429 with ⛔ **no `outcome` at all** — the coarse public floor [decisions 0231, 0243] |
 * | `unreadable` | anything else: a 503, a timeout, a body that is not the contract |
 *
 * ⭐ **The two 429s are told apart by the ABSENCE of `outcome`**, which is the discriminator the api
 * publishes: the floor refuses at the guard, before the handler runs, and answers in the throttler's own
 * shape [`enrolment.model.ts`; 0243].
 */
export type EnrolmentOutcome =
  /**
   * `venueName` is the venue's `displayName` — *Allentown, PA · DC 3* — and it is what *09 · Ready* names
   * beside the kiosk's own name [KAD-6d AC-5].
   *
   * ⛔ **`undefined` does NOT make the enrolment unreadable**, where a missing `published` does. `published`
   * decides the fork and a wrong guess sends a candidate to an unpublished venue; the venue's name is a
   * sentence on a confirmation screen, and refusing a whole enrolment over it would strand a staffer at a
   * tablet that is, in fact, enrolled. The Ready line renders the part it has and invents nothing.
   */
  | {
      readonly kind: 'enrolled';
      readonly published: boolean;
      readonly venueName: string | undefined;
      /**
       * ⭐ **The language this venue's kiosks wake up in** [03-us-kad-1e AC-5, AC-14; decision 0104] —
       * `venue.defaultLanguage`, on the answer the tablet already gets.
       *
       * ⛔ **Never `undefined`, unlike {@link EnrolmentOutcome.venueName} above**, and the difference is
       * deliberate: a name is a sentence on a confirmation screen, while the language decides what a
       * candidate READS. So the one fallback lives here, at the boundary, and ⛔ **nothing downstream
       * carries a second one** — an absent, unknown or unreadable value is `en` [AC-18], which is the
       * language the product already wakes up in [KID-1; `dictionary.ts:15`]. See {@link readVenueLanguage}.
       */
      readonly defaultLanguage: Language;
    }
  /**
   * How many attempts this browser has left, as the server counted them — never as the device did.
   *
   * `undefined` is the contract's `null`: **nothing counted this attempt**. It is not zero and it is not a
   * lock; the api sends it when the browser holds no enrolment token or when layer 1's store failed open.
   */
  | { readonly kind: 'incorrect-pin'; readonly attemptsLeft: number | undefined }
  | { readonly kind: 'device-locked' }
  | { readonly kind: 'throttled' }
  | { readonly kind: 'rate-limited' }
  | { readonly kind: 'unreadable' };

/**
 * Sends the six digits and NOTHING else [AC-2].
 *
 * ⛔ No venue — the PIN carries the configuration [0148] and the api's `forbidNonWhitelisted` refuses the
 * field. ⛔ No name — it does not exist yet, and naming is `PUT /enrolment/name`, behind this credential
 * [KAD-6d].
 *
 * Never throws: every failure is a value, so a screen cannot be left mid-render by an exception.
 */
export async function enrolDevice(pin: string): Promise<EnrolmentOutcome> {
  let response: Response;
  try {
    response = await credentialedFetch(`${apiBaseUrl()}${ENROL_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (cause: unknown) {
    // ⛔ The PIN is never in the log, and neither is anything else about the attempt.
    console.error({
      msg: 'the enrolment request never completed — the staffer sees the inline error and the device stays unenrolled',
      alert: 'enrolment_request_failed',
      cause: cause instanceof Error ? cause.name : undefined,
    });
    return { kind: 'unreadable' };
  }

  /*
   * One read, one shape. Everything below asks the same record questions, and a body that is not an object
   * at all — a gateway's HTML, an empty 500, a JSON `null` — answers all of them the same way: nothing.
   */
  const body = asRecord(await response.json().catch(() => undefined));

  if (response.ok) {
    const published = readPublished(body);
    // ⛔ A 200 whose body is not the contract is NOT an enrolment. Treating it as one would hand a device to
    // the candidate journey on the strength of a status code.
    return published === undefined
      ? { kind: 'unreadable' }
      : {
          kind: 'enrolled',
          published,
          venueName: readVenueName(body),
          defaultLanguage: readVenueLanguage(body, 'connect'),
        };
  }

  /*
   * ⭐ **Branch on `outcome`, never on the number** — the api says so in as many words, and it is what makes
   * the floor's flat 429 distinguishable from layer 2's. A refusal we cannot read is `unreadable`, not a
   * guess: *"do not guess locked, do not guess wrong PIN"* [KAD-6c § *The invariant*, failure posture].
   */
  switch (readOutcome(body)) {
    case ENROLMENT_REFUSAL.incorrectPin:
      return { kind: 'incorrect-pin', attemptsLeft: readAttemptsLeft(body) };
    case ENROLMENT_REFUSAL.deviceLocked:
      return { kind: 'device-locked' };
    case ENROLMENT_REFUSAL.throttled:
      return { kind: 'throttled' };
    default:
      return response.status === 429 ? { kind: 'rate-limited' } : { kind: 'unreadable' };
  }
}

/** Validate at the boundary: a generated type is a claim about the contract, not a runtime guarantee. */
function asRecord(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
}

function readPublished(body: Record<string, unknown>): boolean | undefined {
  return typeof body.published === 'boolean' ? body.published : undefined;
}

/**
 * `venue.displayName`, when the body actually carries one.
 *
 * Two hops, both checked, because `body` is `unknown` until proved otherwise — and an empty string is
 * treated as absent: *"connected to "* with nothing after it is worse than the shorter sentence.
 */
function readVenueName(body: Record<string, unknown>): string | undefined {
  const venueName = displayNameOf(body);
  if (venueName !== undefined) return venueName;

  /*
   * ⭐ **The degradation is deliberate, and it is not silent** — same terms as `enrolment_attempt_uncounted`
   * above, where a control is absent while every screen still looks right.
   *
   * *09 · Ready* is the ONE moment a human confirms the PIN bound the right venue, and under [0148] the
   * venue comes only from the PIN — so a device bound to the wrong one puts candidates in front of another
   * site's orders. Without the line nobody can catch that, and without this nobody can see that the line
   * went missing. ⛔ Nothing identifying is logged: this says only that the answer carried no venue name.
   */
  console.error({
    msg: 'the enrolment answered with NO venue display name — Ready cannot name the venue the PIN bound',
    alert: 'enrolment_venue_unnamed',
  });
  return undefined;
}

/** `venue.displayName` when the body carries a non-empty one — read quietly; callers decide what absence means. */
function displayNameOf(body: Record<string, unknown>): string | undefined {
  const venue = asRecord(body.venue);
  return typeof venue.displayName === 'string' && venue.displayName.length > 0
    ? venue.displayName
    : undefined;
}

/** The kiosk's `name` when the body carries a non-empty one; `null` (not yet named) reads as absent. */
function kioskNameOf(body: Record<string, unknown>): string | undefined {
  return typeof body.name === 'string' && body.name.length > 0 ? body.name : undefined;
}

/**
 * ⭐ **`venue.defaultLanguage` — the one place this app decides what a venue's kiosks wake up in**
 * [03-us-kad-1e AC-14, AC-18; decision 0104].
 *
 * ⛔ **It fails to ENGLISH, loudly in the logs and silently on screen** [AC-18; § *The invariant*]. An
 * answer with no language, a language this app does not know (`fr`), or a body it could not read at all is
 * ⛔ **never an error screen** — those belong to the device's own states [03-us-kad-6i] and mean something
 * else — and ⛔ never blank and never a raw key. It is English and the candidate carries on.
 *
 * ⭐ **The value is matched against {@link LANGUAGE}, the app's ONE definition of the two languages**
 * [`dictionary.ts:12`] — ⛔ not a second union beside it, and not a cast. `EN` is not `en`: the api's own
 * column is constrained to the two lowercase values and refuses anything else with a 400 [AC-4], so a
 * different casing arriving here is an answer this app cannot read, not one to normalise.
 *
 * ⚠️ **`error`, and a previous revision of this function was wrong to make it `warn`.** That level cited
 * [`0237`, `0308`], which govern the **api's** server-side logs — and 0237 itself records that even those
 * `alert:` lines reach no queryable workspace. ⛔ **Nothing consumes an `alert:` line from THIS app today**
 * [`kioskFetch.ts`, `arrivalReason.ts`], so there is no alert budget to protect and no flood to cause:
 * what the level decides is whether a person tailing a kiosk's console sees it at all. ⭐ **And the
 * sibling thirty lines up logs a missing venue NAME — a sentence on a staff screen — at `error`**, while
 * this one decides what language every candidate at the venue reads. The more consequential degradation
 * cannot carry the lower level.
 *
 * ⭐ **`trigger` and `answered` are what make a repeat readable**, the same discipline
 * {@link reportProbeUnreadable} records for the probe. ⛔ **Without them, *the api half is not deployed
 * yet* — expected, estate-wide, self-resolving — and *the api regressed and every Spanish venue is serving
 * English* produce byte-identical lines.** `answered: 'undefined'` is the first (no field on the wire);
 * `answered: 'string'` is the second (the api sent a value this app rejected). ⛔ Nothing identifying is
 * logged, and ⛔ never the value itself: a language code is not a secret, but this prints on a shared
 * machine's console in a lobby and the shape of every other emitter here is the same.
 */
function readVenueLanguage(body: Record<string, unknown>, trigger: LanguageAnswerSource): Language {
  const answered = asRecord(body.venue).defaultLanguage;
  const known = Object.values(LANGUAGE).find((language) => language === answered);
  if (known !== undefined) return known;

  console.error({
    msg: 'the enrolment carried no language this app can read — every candidate at this venue reads English [KAD-1e AC-18]',
    alert: 'enrolment_venue_language_unreadable',
    trigger,
    answered: typeof answered,
  });
  return LANGUAGE.en;
}

function readOutcome(body: Record<string, unknown>): EnrolmentRefusalOutcome | undefined {
  return Object.values(ENROLMENT_REFUSAL).find((known) => known === body.outcome);
}

/**
 * ⛔ **`retryAfterSeconds` is deliberately NOT read.**
 *
 * Layer 2's refusal has no drawn screen [KAD-6 § G6 item 5] and AC-6 forbids borrowing layer 1's. A number
 * the app holds is a number somebody renders, and what that renders is a countdown nobody designed — so the
 * field stays on the wire until the frame exists.
 */
function readAttemptsLeft(body: Record<string, unknown>): number | undefined {
  if (typeof body.attemptsLeft === 'number') return body.attemptsLeft;

  /*
   * ⭐ **`null` here is the contract's *nothing counted this attempt*, and it is worth saying out loud.**
   * A wrong PIN that advances no counter means [0203]'s layer 1 has no subject — no token, or its store
   * failed open — so the device can be ground indefinitely and the lock the design draws will never
   * engage. ⛔ Nothing identifying is logged: this says only that a counter did not move.
   */
  console.error({
    msg: 'a refused PIN attempt counted against NOTHING — the three-strike device lock has no subject',
    alert: 'enrolment_attempt_uncounted',
  });
  return undefined;
}

/**
 * What the server said about a typed kiosk name [03-us-kad-6d AC-3; decisions 0118, 0149].
 *
 * | | |
 * | --- | --- |
 * | `named` | 200. The name the server STORED, echoed back — what *09 · Ready* renders |
 * | `taken` | 409, and ⛔ **409 means exactly one thing here**: another live enrolment at THIS venue holds it |
 * | `unreadable` | anything else: a 503, a timeout, a body that is not the contract |
 *
 * ⛔ **A 401 is not in this union**, and its absence is the point: it means the credential no longer names a
 * live enrolment, which is the same verdict as *this device is not enrolled* — so it is handled where every
 * other one is, by `kioskFetch` returning the app to the PIN pad [decision 0245]. It still surfaces here as
 * `unreadable` so a caller cannot fall through a switch, but the navigation has already happened.
 */
export type KioskNameOutcome =
  | { readonly kind: 'named'; readonly name: string }
  | { readonly kind: 'taken' }
  | { readonly kind: 'unreadable' };

/**
 * Sends the typed name, and NOTHING else [AC-6; decision 0204].
 *
 * ⛔ **No venue and no kiosk id.** Both travel on the credential, and the api's `forbidNonWhitelisted`
 * refuses either field.
 *
 * ⭐ **It goes through `kioskFetch`, unlike the two public enrolment routes above** — and the difference is
 * what a 401 MEANS on each. On `POST /enrolment` it is *that PIN was wrong* [0090], so routing it to the PIN
 * pad would be a screen answering itself. Here it is *the credential no longer names a live enrolment*,
 * which is exactly the verdict `kioskFetch` exists to act on.
 *
 * ⭐ **Non-idempotent-call rule** [04-eng-kiosk-conventions § *The frontends' network calls*; decision
 * 0284]: this `PUT` is **idempotent server-side** — it sets a column on the enrolment the credential names,
 * so replaying it writes the same value — **and the caller does not retry**. Two of the three conditions,
 * where one is required.
 *
 * Never throws: every failure is a value, so a screen cannot be left mid-render by an exception.
 */
export async function nameKiosk(name: string): Promise<KioskNameOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}${NAME_PATH}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (cause: unknown) {
    // ⛔ The name is never in the log: it is a staffer's free text on a shared device's console.
    console.error({
      msg: 'the naming request never completed — the staffer sees the inline error and the kiosk stays unnamed',
      alert: 'kiosk_name_request_failed',
      cause: cause instanceof Error ? cause.name : undefined,
    });
    return { kind: 'unreadable' };
  }

  /*
   * ⭐ **409 is read from the STATUS, not from a body field**, because that is all the api sends: the
   * handler raises a bare `ConflictException`, so the response carries Nest's own shape and no `outcome`
   * discriminator to branch on. ⛔ And it is the only thing 409 means on this route.
   */
  if (response.status === 409) return { kind: 'taken' };

  if (!response.ok) {
    reportNamingFailure(`the route answered ${String(response.status)}`, response.status);
    return { kind: 'unreadable' };
  }

  /*
   * ⭐ **The stored name is read back rather than assumed** [AC-6: stored as typed]. A 200 whose body is not
   * the contract is not a naming: showing *Ready* on the strength of a status code would tell a staffer the
   * tablet carries a name the server may never have written.
   */
  const stored = asRecord(await response.json().catch(() => undefined)).name;
  if (typeof stored === 'string' && stored.length > 0) return { kind: 'named', name: stored };

  reportNamingFailure('the 200 did not carry the stored name', response.status);
  return { kind: 'unreadable' };
}

/**
 * ⭐ **Naming is a HARD GATE between an enrolled device and the candidate journey**, so a route that breaks
 * has to leave a trace.
 *
 * Every staffer in the estate would otherwise read the same unactionable sentence — *"We are having some
 * issues"*, which claims no outcome by design [0262] — with nothing on any console to say the naming route
 * is the thing that is down. Same shape and same discipline as `reportTokenFailure` above.
 *
 * ⛔ **The name is never logged, and neither is anything else about the device**: this fires on a shared
 * machine's console in a lobby. The status and the reason are the whole payload.
 */
function reportNamingFailure(reason: string, status: number): void {
  console.error({
    msg: 'the naming route did not answer with a stored name — the kiosk cannot be named and cannot take candidates',
    alert: 'kiosk_name_unreadable',
    reason,
    status,
  });
}

/**
 * What the server said when the app asked, on THIS load, whether this device is still enrolled
 * [03-us-kad-6i AC-7; decision 0280].
 *
 * | | |
 * | --- | --- |
 * | `enrolled` | 200 whose body IS the contract. `published` forks the flow, exactly as the connect's does |
 * | `unenrolled` | 401 — the credential names no live enrolment: a removed device, a revoked record, or a browser that never enrolled |
 * | `unreadable` | 503, 429, a timeout, a body that is not the contract, or any status the contract does not name |
 *
 * ⛔ **`unreadable` is NOT an enrolment verdict, and the three answers above are the only ones there are**:
 * *we cannot say* is neither *you are enrolled* nor *you are not* [AC-10; KAD-6c § *The invariant*, failure
 * posture]. ⛔ **A 429 is one of them** — the probe is exempt from the coarse floor [`0287`], so one means
 * the skip is missing or an edge appliance refused, ⛔ never that the device is or is not enrolled.
 *
 * ⛔ **`name` is on the wire and is read by NOTHING here** [AC-8; Operator, 2026-09-16]. The naming step is
 * [03-us-kad-6d]'s, and the shape that would hold a name at boot is KAD-6d's to choose — so
 * {@link EnrolmentOutcome} is not widened and this type does not carry one either.
 */
/**
 * What asked for an enrolment probe — ⭐ **the discriminator an operator reads off the console** [AC-13].
 *
 * ⛔ **Without it every probe failure emits one indistinguishable line**, and *"this tablet recovered itself
 * nine times"* is unreadable from *"a staffer stood at this tablet pressing Try again nine times"* — which
 * is the difference between *the branch is fine* and *send somebody*.
 *
 * It lives beside the call rather than beside the store so the dependency keeps its direction: `lib` reads
 * this module's types, and this module reads none of `lib`'s.
 */
/**
 * Who asked: the load itself, a person's *Try again*, or the environment coming back [03-us-kad-6i AC-10]
 * — and, on an enrolled tablet, a visit ending (`reset`) or the idle welcome's first tap (`tap`)
 * [03-us-kad-6o AC-1, AC-2; decisions 0433, 0430].
 * ⛔ The live socket asks nothing since [03-us-kad-6e]: there is no *nothing published* state to re-check,
 * and [0430] § 2 keeps it that way.
 */
export type ProbeTrigger = 'boot' | 'pressed' | 'signal' | 'reset' | 'tap';

export type EnrolmentProbeOutcome =
  /**
   * ⭐ **`defaultLanguage` is on BOTH answers because ONE model answers both routes** [03-us-kad-1e AC-5;
   * `enrolment.model.ts`]. ⛔ **A field the connect carries and the probe does not would be a tablet that
   * wakes in the venue's language when a staffer types six digits and in English on every reload after** —
   * the exact fork the api's *"one model"* rule exists to prevent.
   */
  | {
      readonly kind: 'enrolled';
      readonly published: boolean;
      readonly defaultLanguage: Language;
      /**
       * ⭐ **Display only — the boot never forks on either** [03-us-kad-6i AC-8]. The venue's `displayName`
       * and the kiosk's `name`, re-read on every load so the *Need help?* box's *Site* row survives a
       * reload [03-us-kid-7 O4; 0441 point 5]. `undefined` when the body carries none.
       */
      readonly venueName?: string;
      readonly kioskName?: string;
    }
  | { readonly kind: 'unenrolled' }
  | { readonly kind: 'unreadable' };

/**
 * Asks the server whether this device is still enrolled — the one call a boot makes [AC-7; decision 0280],
 * and the one an enrolled tablet repeats at every return to the welcome and on its first tap [03-us-kad-6o;
 * 0433; 0430]. The {@link ProbeTrigger} says which.
 *
 * ⛔ **`credentialedFetch`, NEVER `kioskFetch`, and this is the criterion rather than a preference**
 * [AC-9; `0285`]. `kioskFetch` reports a 401 to {@link reportUnauthenticated}, which calls
 * `refuseCredential()` and puts *"Incorrect PIN"* in front of a staffer who did nothing but power a tablet
 * on. ⭐ **A boot 401 is the PLAIN pad**: nobody typed anything, so there is nothing to explain.
 *
 * ⛔ **`cache: 'no-store'`, and it is not decoration** [AC-12; KAD-6h AC-7]. `GET /enrolment/me` is the
 * first cacheable GET in this flow — `/enrolment/session` answers 204 and `POST /enrolment` is not
 * cacheable — so a branch proxy could serve one tablet's 200 to another tablet's boot: a wrong *enrolled*
 * for a removed device, and venue **A**'s enrolment to a device that is not at A. ⭐ **And a cached 200 IS
 * the device answering the question**, which is the one thing the invariant forbids.
 *
 * ⛔ **200 AND NOTHING ELSE, then the BODY** — the same trap {@link issueEnrolmentToken} guards with
 * `status === 204` [AC-14]. `staticwebapp.config.json` rewrites an unknown path to `index.html` with a
 * **200**, so an app deployed against an api that predates [03-us-kad-6h] would read `published` off a page
 * of HTML and hand a tablet to the candidate journey on the strength of a status code.
 *
 * Never throws: every failure is a value, so a boot cannot be left mid-render by an exception.
 */
export async function probeEnrolment(trigger: ProbeTrigger): Promise<EnrolmentProbeOutcome> {
  let response: Response;
  try {
    response = await credentialedFetch(`${apiBaseUrl()}${ME_PATH}`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch (cause: unknown) {
    reportProbeUnreadable('the request never completed', cause, trigger);
    return { kind: 'unreadable' };
  }

  if (response.status === 401) {
    /*
     * ⭐ **The one trace this path leaves on a 401** [AC-13]. `credentialedFetch` logs nothing and
     * `kioskFetch` is forbidden here, so without this line the single most consequential thing a boot does
     * — deciding that a tablet is no longer enrolled — is invisible. ⛔ **Nothing identifying**: no
     * `kioskId`, no venue, no cookie value, no PIN.
     */
    console.error({
      msg: 'the enrolment probe was REFUSED — this device holds no live enrolment and the plain PIN pad renders',
      alert: 'boot_probe_unenrolled',
      trigger,
    });
    return { kind: 'unenrolled' };
  }

  if (response.status !== 200) {
    reportProbeUnreadable(`the route answered ${String(response.status)}`, undefined, trigger);
    return { kind: 'unreadable' };
  }

  /*
   * ⛔ **A 200 whose body is not the contract is NOT an enrolment**, following `enrolDevice`'s own rule:
   * treating it as one would hand a device to the candidate journey on the strength of a status code.
   */
  const body = asRecord(await response.json().catch(() => undefined));
  const published = readPublished(body);
  if (published === undefined) {
    reportProbeUnreadable('the 200 did not carry the contract', undefined, trigger);
    return { kind: 'unreadable' };
  }

  /*
   * ⭐ **The venue's language is read on the RELOAD path too, and that is [KAD-1e] AC-17's whole mechanism**:
   * a default an administrator changed is in force at this tablet's next load, because every load asks.
   * ⛔ **No poll, no second probe and no socket** — there is no transport [0270] and that is KAD-6f's.
   *
   * ⛔ **A missing language does NOT make the answer unreadable**, where a missing `published` does: one
   * decides whether a candidate is served at all, the other what they read — and refusing the enrolment
   * over it would strand a tablet that is, in fact, enrolled [AC-18].
   */
  /*
   * ⭐ **The names are read for DISPLAY and nothing else** — the fork above is still `published` alone
   * [AC-8], and an absent name changes no route [KAD-6i G4 stays KAD-6d's]. Without them a reloaded tablet
   * forgets where it is: the stores keep nothing in web storage [AC-12], and only the connect and the naming
   * step wrote them, so the *Need help?* box's *Site* row went empty [03-us-kid-7 O4; 0441 point 5].
   */
  const venueName = displayNameOf(body);
  const kioskName = kioskNameOf(body);
  return {
    kind: 'enrolled',
    published,
    defaultLanguage: readVenueLanguage(body, trigger),
    ...(venueName === undefined ? {} : { venueName }),
    ...(kioskName === undefined ? {} : { kioskName }),
  };
}

/**
 * ⭐ **The probe's second alert, and it is a DIFFERENT name from the 401's on purpose** [AC-13]. ⚠️ The
 * `alert` keys keep their `boot_probe_` prefix although a re-check [03-us-kad-6o] raises them too: alert
 * rules consume those names, and `trigger` already says which call it was.
 *
 * One says *the server answered, and the answer is no*; this one says *nothing answered, so the tablet knows
 * nothing* — and only the second means somebody should look at a deployment. Same `msg` · `alert` · `cause`
 * shape as {@link reportTokenFailure}, so one format reads from both. ⛔ **Nothing identifying**: no
 * `kioskId`, no venue, no cookie value, no PIN.
 *
 * ⭐ **`trigger` is what makes a repeat readable.** Nine of these from one tablet mean one thing when they
 * say `signal` — it recovered itself nine times and the branch is fine — and quite another when they say
 * `pressed`, which is a staffer standing at a tablet pressing a button nine times. ⛔ Without it the two are
 * the same line.
 */
function reportProbeUnreadable(reason: string, cause: unknown, trigger: ProbeTrigger): void {
  console.error({
    msg: 'the enrolment probe could NOT be read — the tablet claims no enrolment outcome',
    alert: 'boot_probe_unreadable',
    reason,
    trigger,
    cause: cause instanceof Error ? cause.name : undefined,
  });
}
